import { useCallback, useEffect, useMemo, useState } from 'react';

import type { FilterOption } from '@shared/lib/types';
import type { DateFilterValue } from '@shared/ui/vanilla-calendar/lib/types';
import { useGetCreditTransactionsListQuery } from '@shared/api';
import { useAppSelector } from '@shared/redux';
import { authSelectors } from '@shared/redux/ducks/auth';
import type { CreditTransactionType } from '@shared/api/endpoints/credit/credit-endpoints';

interface UseCreditTransactionsProps {
  periodValue: FilterOption<DateFilterValue>[] | null;
  types?: CreditTransactionType[];
  pageSize?: number;
  skip?: boolean;
}

export const useCreditTransactions = ({
  periodValue,
  types = [],
  pageSize = 20,
  skip = false,
}: UseCreditTransactionsProps) => {
  const [currentPage, setCurrentPage] = useState(1);

  const currentOrganization = useAppSelector(authSelectors.selectCurrentOrganizationInfo);
  const organizationId = currentOrganization?.id ?? 0;

  const dateFrom = periodValue?.[0]?.value?.startDate ?? '';
  const dateTo = periodValue?.[0]?.value?.endDate ?? '';
  const typesKey = useMemo(() => [...types].sort().join(','), [types]);

  useEffect(() => {
    setCurrentPage(1);
  }, [dateFrom, dateTo, typesKey, organizationId]);

  const queryArgs = useMemo(
    () => ({
      offset: Math.max(0, (currentPage - 1) * pageSize),
      limit: pageSize,
      organizationId,
      filters: {
        date_from: dateFrom,
        date_to: dateTo,
        types,
      },
    }),
    [currentPage, pageSize, organizationId, dateFrom, dateTo, types],
  );

  const { data, isLoading, isFetching, isError, refetch } = useGetCreditTransactionsListQuery(
    queryArgs,
    {
      skip: skip || !organizationId || !dateFrom || !dateTo,
    },
  );

  const transactions = data?.items ?? [];
  const count = data?.count ?? 0;
  const hasMore = transactions.length < count;

  const fetchNextData = useCallback(() => {
    if (!hasMore || isFetching || isLoading) return;
    setCurrentPage((prev) => prev + 1);
  }, [hasMore, isFetching, isLoading]);

  return {
    transactions,
    count,
    isLoading,
    isFetching,
    isError,
    hasMore,
    fetchNextData,
    refetch,
  };
};
